import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { CartOverlayDynamicService } from '@app/overlays/cart-overlay/services/cart-overlay-dynamic.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { NgIf } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { CartPromoOverlayDynamicService } from '@app/overlays/cart-promo-overlay/services/cart-promo-overlay-dynamic.service';
import { BuybackOverlayDynamicService } from '@app/buyback/views/buyback-overlay/services/buyback-overlay-dynamic.service';

@Component({
  selector: 'popsy-add-to-cart-button',
  templateUrl: './add-to-cart-button.template.html',
  styleUrls: ['./add-to-cart-button.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, NgIf],
})
export class AddToCartButtonComponent implements OnDestroy {
  @Input() public product?: Product | null;

  @Output() public readonly addToCart: EventEmitter<boolean>;

  public isBrowser: boolean;

  public showingCartOverlay: boolean;
  private openCartOverlaySubscription?: Subscription | null;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly akitaCartService: AkitaCartService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly cartOverlayDynamicService: CartOverlayDynamicService,
    private readonly cartPromoOverlayDynamicService: CartPromoOverlayDynamicService,
    private readonly buybackOverlayDynamicService: BuybackOverlayDynamicService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.addToCart = new EventEmitter();
    this.showingCartOverlay = false;
    this.isBrowser = this.akitaRouterQuery.isBrowser;
  }

  public onAddToCart(event: Event): boolean {
    event.stopPropagation();
    event.preventDefault();
    if (this.product?.id) {
      this.akitaCartService.addNewProductToCartAsync(
        this.product,
        this.product.selectedInsurance
      );
      this.addToCart.emit(true);
      // this.openCartPromo();
      this.openBuybackPromo();
    }

    return false;
  }

  public openCart(): void {
    if (this.openCartOverlaySubscription) {
      this.openCartOverlaySubscription.unsubscribe();
    }

    const validLocale = this.akitaProductsQuery.validateLocale();
    if (validLocale.country === 'US') {
      // shopify cart opening not needed
    } else {
      this.openCartOverlaySubscription = this.cartOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.showingCartOverlay = Boolean(data);
            this.changeDetector.markForCheck();
          },
        });
      this.cartOverlayDynamicService.open({
        data: {},
        isBrowser: this.isBrowser,
      });
    }
  }

  public openCartPromo(): void {
    if (this.openCartOverlaySubscription) {
      this.openCartOverlaySubscription.unsubscribe();
    }

    const validLocale = this.akitaProductsQuery.validateLocale();
    if (validLocale.country === 'US') {
      // shopify cart opening not needed
    } else {
      this.openCartOverlaySubscription = this.cartPromoOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.showingCartOverlay = Boolean(data);
            this.changeDetector.markForCheck();
          },
        });
      this.cartPromoOverlayDynamicService.open({
        data: { product: this.product },
        isBrowser: this.isBrowser,
      });
    }
  }

  public openBuybackPromo(): void {
    if (this.openCartOverlaySubscription) {
      this.openCartOverlaySubscription.unsubscribe();
    }

    this.openCartOverlaySubscription = this.buybackOverlayDynamicService.observe
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (data?: any | null) => {
          this.showingCartOverlay = Boolean(data);
          this.changeDetector.markForCheck();

          if (!this.showingCartOverlay) {
            this.openCartPromo();
          }
        },
      });
    this.buybackOverlayDynamicService.open({
      data: {},
      isBrowser: this.isBrowser,
    });
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
